import React, { memo, useCallback, useMemo } from 'react'
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro'
import { useSelector } from 'react-redux'
import { EmptyAiReviewConfigs } from 'src/components/ai-review/grid/EmptyAiReviewConfigs'
import { GridTable } from 'src/components/shared/grid/GridTable'
import {
  AI_REVIEW_CONFIGS_GRID_COLUMNS_STORAGE_KEY,
  aiReviewConfigPageSizeOptions,
} from 'src/constants/ai-review/constants'
import { AiReviewConfigsTableFields } from 'src/constants/ai-review/enums'
import { buildAiReviewConfigColumns } from 'src/constants/ai-review/aiReviewConfigsGrid'
import type { AiDocumentReviewConfig } from 'src/interfaces/aiDocumentReviewConfig'
import {
  getAiDocumentReviewConfigs,
  getAiDocumentReviewConfigsLoading,
} from 'src/selectors/aiDocumentReviewConfig'
import { useGridColumns } from 'src/utils/hooks/grid/useGridColumns'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

interface Props {
  canManage: boolean
  onEdit(config: AiDocumentReviewConfig): void
  onDelete(config: AiDocumentReviewConfig): void
}

const getRowId = (row: AiDocumentReviewConfig) => {
  return row.id
}

// The document identifies the row and the actions column has no meaning when hidden; both also
// carry `hideable: false`, which is what removes them from the manage-columns panel. This guard
// additionally stops a programmatic visibility-model change from persisting a hide.
const UNHIDEABLE_COLUMNS: Array<string> = [
  AiReviewConfigsTableFields.stepType,
  AiReviewConfigsTableFields.actions,
]

const AiReviewConfigsTableComponent = ({ canManage, onEdit, onDelete }: Props) => {
  const configs = useSelector(getAiDocumentReviewConfigs)
  const isLoading = useSelector(getAiDocumentReviewConfigsLoading)

  const { paginationModel, setPaginationModel } = useGridPagination(aiReviewConfigPageSizeOptions)

  // A fresh array each render would reset the grid's column state, so the factory call is memoized.
  const configColumns = useMemo(() => {
    return buildAiReviewConfigColumns(onEdit, onDelete, canManage)
  }, [onEdit, onDelete, canManage])

  const { includedColumns, handleAddExcludedColumn, handleRemoveExcludedColumn } = useGridColumns(
    configColumns,
    AI_REVIEW_CONFIGS_GRID_COLUMNS_STORAGE_KEY,
  )

  const columnVisibilityModel = useMemo(() => {
    return configColumns.reduce<GridColumnVisibilityModel>((accumulator, column) => {
      accumulator[column.field] = includedColumns.includes(column.field)

      return accumulator
    }, {})
  }, [configColumns, includedColumns])

  // `useGridColumns` stores excluded fields as a flat list; the grid speaks a visibility model.
  // Translate both ways so menu changes persist to localStorage across reloads.
  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      configColumns.forEach((column) => {
        if (UNHIDEABLE_COLUMNS.includes(column.field)) {
          return
        }

        if (model[column.field] === false) {
          handleAddExcludedColumn(column.field)

          return
        }

        handleRemoveExcludedColumn(column.field)
      })
    },
    [configColumns, handleAddExcludedColumn, handleRemoveExcludedColumn],
  )

  return (
    <GridTable<AiDocumentReviewConfig>
      rows={configs}
      columns={configColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={aiReviewConfigPageSizeOptions}
      initialState={{
        sorting: { sortModel: [{ field: AiReviewConfigsTableFields.stepType, sort: 'asc' }] },
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      EmptyElement={EmptyAiReviewConfigs}
      tableDataTestId="ai-review-configs-table"
      rowDataTestIdPrefix="ai-review-configs"
    />
  )
}

export const AiReviewConfigsTable = memo(AiReviewConfigsTableComponent)
